import { Box, Typography } from '@mui/material'
import React from 'react'
import { Trans } from 'react-i18next'
import { ID, Port } from '../../types/models'
import { PortConfigRelay, RelayConfig, RelayPort } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { OptimizerControlPortControl } from './OptimizerControlPortControl'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'

/**
 * Controller for relay or switch.
 */
export class RelayPortControl extends PortControl<RelayPort, RelayConfig> {

  port: RelayPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as RelayPort
  }

  /**
   * Find the corresponding optimizer control.
   */
  optimizerControl(): OptimizerControlPortControl | null {
    const portNumber = this.port.port_number
    const ctrl = this.deviceControl.findPortBy(portControl =>
      portControl.port.type === 'OPTIMIZER_CONTROL' &&
      (portControl as OptimizerControlPortControl).getConfig('port') === portNumber)
    return ctrl
      ? ctrl as OptimizerControlPortControl
      : null
  }

  get isHighVoltage(): boolean {
    const voltage = this.port.spec?.voltage || 0
    return voltage > 50
  }

  get group(): string { return this.app.t('PortGroup-Relays') }

  getTypeName(): string { return this.app.t('PortType-Relay Control') }

  getName(): string {
    return this.isHighVoltage
      ? this.app.t('PortName-Relay {number}').replace('{number}', `${this.number}`)
      : this.app.t('PortName-Relay {number} (50V)').replace('{number}', `${this.number}`)
  }

  getDescription(): string {
    return this.isHighVoltage
      ? this.app.t('High-voltage relay')
      : this.app.t('Low-voltage relay')
  }

  isOn(): boolean {
    return !!this.getValue('is_on')
  }

  isOff(): boolean {
    return !this.isOn()
  }

  // Relay connection may or may not be ready for control (e.g. EV charging connector), default to "Connected" (always assumed connected)
  getConnectionStatus(): string {
    return 'Connected'
  }

  turnOn(): void {
    this.sendCommand({ name: 'is_on', value: true })
  }

  turnOff(): void {
    this.sendCommand({ name: 'is_on', value: false })
  }

  getAssociatedPortIDs(): ID[] {
    const optCtrl = this.optimizerControl()
    if (!optCtrl) {
      throw new Error(`Cannot find optimizer control port for port #${this.port.id} ${this.port.port_number}`)
    }
    return [this.port.id as ID, optCtrl.port.id as ID]
  }

  getConfigDefaults(): PortConfigRelay {
    return {
      enabled: false,
      invert: false,
      failsafe_enable: false,
      failsafe_state: true,
    }
  }

  configTopLeft(): React.ReactNode {
    const invert = this.getConfig('invert')
    return (
      <Box sx={{ my: 2 }}>
        <Trans>Relay Inversion</Trans>
        <Box sx={{ display: 'flex' }}>
          <CustomSwitch data-cy="switch-Inverted" onClick={() => this.setConfig('invert', !invert)} checked={!!invert} sx={{ ml: '1rem' }}/>
          <Typography component="span" sx={{ ml: 2 }}>
          {invert ? <Trans>Appliance is OFF when relay is ON.</Trans> : <Trans>Appliance is ON when relay is ON.</Trans>}
          </Typography>
        </Box>
    </Box>)
  }

  configTopRight(): React.ReactNode {
    const failsafeEnable = this.getConfig('failsafe_enable')
    const failsafeState = this.getConfig('failsafe_state')
    return (
      <Box sx={{ my: 2 }}>
        <Trans>Failsafe Mode</Trans>
        { false &&
          <Box sx={{ display: 'flex' }}>
            <CustomSwitch data-cy="switch-Failsafe Enable" onClick={() => this.setConfig('failsafe_enable', !failsafeEnable)} checked={!!failsafeEnable} sx={{ ml: '1rem' }}/>
            <Typography component="span" sx={{ ml: 2 }}>
              {failsafeEnable ? <Trans>Failsafe mode is enabled.</Trans> : <Trans>Failsafe mode is disabled.</Trans>}
            </Typography>
          </Box>
        }
        <Box sx={{ display: 'flex' }}>
          <CustomSwitch data-cy="switch-Failsafe State" onClick={() => this.setConfig('failsafe_state', !failsafeState)} checked={!!failsafeState} sx={{ ml: '1rem' }}/>
          <Typography component="span" sx={{ ml: 2 }}>
            {failsafeState ? <Trans>When network connection fails, device is ON.</Trans> : <Trans>When network connection fails, device is OFF.</Trans>}
          </Typography>
        </Box>
    </Box>)
  }
}
